import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect, useMemo, useRef } from 'react'
import QRCode from 'qrcode'
import { Copy, Check, ChevronDown, Download, ExternalLink, Link as LinkIcon, Pencil, Printer, Users, Store, Users2 } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { useEvent, useOrganizationDetail, useShortLink, useShortLinksList } from '@/lib/api/queries'
import { useAuth } from '@/lib/auth'
import { apiClient } from '@/lib/api/client'
import { usePermission } from '@/lib/permissions'
import { AccessDenied } from '@/components/permission-guard'
import { TOOLKIT_RESOURCES, type ResourceAudience } from '@/lib/toolkit/resources'
import {
  POSTER_SIZES,
  buildPosterHtml,
  openPosterPreview,
  safeHexColor,
  type PosterConfig,
  type PosterSize,
} from '@/lib/toolkit/poster'

export const Route = createFileRoute('/_authenticated/events/$eventId/toolkit')({
  component: ToolkitPage,
})

// --- helpers ---------------------------------------------------------------

function triggerDownload(href: string, filename: string) {
  const a = document.createElement('a')
  a.href = href
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
}

function slugify(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

// Public attendee-facing web base. Share links are built entirely client-side:
// event page = {base}/event/{id}, org group page = {base}/groups/{org.slug} (slug
// resolved via useOrganizationDetail). Override with VITE_PUBLIC_WEB_BASE_URL.
const PUBLIC_WEB_BASE = (
  (import.meta.env.VITE_PUBLIC_WEB_BASE_URL as string | undefined) || 'https://pay.eventpay.com'
).replace(/\/$/, '')

// --- Share + QR card -------------------------------------------------------

function ShareCard({
  label,
  description,
  url,
  filenameBase,
  hits,
}: {
  label: string
  description: string
  url?: string | null
  filenameBase: string
  /** Scan/click count from the tracked short link, when available. */
  hits?: number
}) {
  const [svg, setSvg] = useState('')
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    let active = true
    if (!url) {
      setSvg('')
      return
    }
    QRCode.toString(url, { type: 'svg', margin: 1 })
      .then((s) => active && setSvg(s))
      .catch(() => active && setSvg(''))
    return () => {
      active = false
    }
  }, [url])

  const cardClass =
    'bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-4 md:p-5'

  if (!url) {
    return (
      <div className={cardClass}>
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{label}</h3>
        <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
          Not available yet — the public web URL isn’t configured for this environment.
        </p>
      </div>
    )
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* clipboard blocked — ignore */
    }
  }

  const downloadSvg = () => {
    const blob = new Blob([svg], { type: 'image/svg+xml' })
    const href = URL.createObjectURL(blob)
    triggerDownload(href, `${filenameBase}-qr.svg`)
    setTimeout(() => URL.revokeObjectURL(href), 5000)
  }

  const downloadPng = async () => {
    const dataUrl = await QRCode.toDataURL(url, { width: 1024, margin: 1 })
    triggerDownload(dataUrl, `${filenameBase}-qr.png`)
  }

  return (
    <div className={cardClass}>
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{label}</h3>
        {typeof hits === 'number' && (
          <span className="shrink-0 rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-medium tabular-nums text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
            {hits.toLocaleString()} view{hits === 1 ? '' : 's'}
          </span>
        )}
      </div>
      <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{description}</p>

      <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center">
        {/* QR */}
        <div
          className="h-32 w-32 shrink-0 rounded-md bg-white p-2 [&>svg]:h-full [&>svg]:w-full"
          aria-label={`QR code for ${label}`}
          dangerouslySetInnerHTML={{ __html: svg }}
        />

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 rounded-md border border-neutral-200 bg-neutral-50 px-2.5 py-1.5 dark:border-neutral-800 dark:bg-neutral-900">
            <LinkIcon className="h-3.5 w-3.5 shrink-0 text-neutral-400" />
            <span className="truncate text-xs text-neutral-700 dark:text-neutral-200" title={url}>
              {url}
            </span>
          </div>

          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={copy}
              className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? 'Copied' : 'Copy link'}
            </button>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Open
            </a>
            <button
              type="button"
              onClick={downloadSvg}
              className="inline-flex items-center gap-1.5 rounded-md border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800"
            >
              <Download className="h-3.5 w-3.5" /> SVG
            </button>
            <button
              type="button"
              onClick={downloadPng}
              className="inline-flex items-center gap-1.5 rounded-md border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800"
            >
              <Download className="h-3.5 w-3.5" /> PNG
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// --- Poster designer ---------------------------------------------------------

const MM_TO_PX = 3.7795
const SWATCHES = ['#2563eb', '#0f172a', '#ea580c', '#16a34a', '#7c3aed', '#111111']

const inputClass =
  'w-full rounded-md border border-neutral-200 bg-white px-2.5 py-1.5 text-xs text-neutral-900 focus:border-blue-500 focus:outline-none dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100'

function DesignerToggle({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
}) {
  return (
    <label
      className={`flex items-center justify-between gap-2 text-xs ${disabled ? 'opacity-40' : ''} text-neutral-700 dark:text-neutral-200`}
    >
      {label}
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="h-3.5 w-3.5 accent-blue-600"
      />
    </label>
  )
}

function PosterDesigner({
  eventId,
  eventName,
  eventPageUrl,
  groupPageUrl,
  logoUrl,
  accentColor,
}: {
  eventId?: number
  eventName: string
  /** Canonical public event page — the default destination. */
  eventPageUrl?: string | null
  /** Org group page — optional destination. */
  groupPageUrl?: string | null
  logoUrl?: string | null
  accentColor?: string | null
}) {
  const accent = safeHexColor(accentColor, '')
  const swatches = useMemo(
    () => [...new Set(accent ? [accent, ...SWATCHES] : SWATCHES)],
    [accent],
  )
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const [size, setSize] = useState<PosterSize>('a4')
  const [bg, setBg] = useState(accent || '#2563eb')
  const [headline, setHeadline] = useState('SCAN. PAY. ENJOY.')
  const [subline, setSubline] = useState(`${eventName} runs on EventPay.`)

  // --- Destination: what the QR ultimately opens -------------------------
  type DestMode = 'event' | 'group' | 'custom'
  const [destMode, setDestMode] = useState<DestMode>('event')
  const [customDest, setCustomDest] = useState('')
  const destination =
    destMode === 'event' ? (eventPageUrl ?? '') :
    destMode === 'group' ? (groupPageUrl ?? '') :
    customDest
  // Debounce so typing a custom URL doesn't re-point on every keystroke.
  const [debouncedDest, setDebouncedDest] = useState(destination)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedDest(destination), 400)
    return () => clearTimeout(timer)
  }, [destination])
  const validDest = /^https?:\/\//i.test(debouncedDest)

  // --- Tracking: QR encodes the short link; destination stays editable ----
  const [tracking, setTracking] = useState(true)
  const SIZE_SOURCE: Record<PosterSize, string> = {
    a4: 'poster',
    postcard: 'postcard',
    'business-card': 'card',
  }
  const link = useShortLink(
    eventId,
    SIZE_SOURCE[size],
    tracking && validDest ? debouncedDest : undefined,
  )
  const qrTarget = !validDest
    ? ''
    : tracking
      ? (link.data?.short_url ?? debouncedDest)
      : debouncedDest

  // --- Printed link text: humans read THIS, not the QR --------------------
  const [printedText, setPrintedText] = useState('')
  const [textDirty, setTextDirty] = useState(false)
  useEffect(() => {
    if (!textDirty) setPrintedText(destination)
  }, [destination, textDirty])

  // --- Custom code editing (locked server-side after first scan) ----------
  const [editingCode, setEditingCode] = useState(false)
  const [codeInput, setCodeInput] = useState('')
  const [codeError, setCodeError] = useState('')
  const [codeSaving, setCodeSaving] = useState(false)
  // An in-progress code edit must never carry over to a different link
  // (size switch = different source; destination/tracking switch = different
  // context) — it would rename the wrong link.
  useEffect(() => {
    setEditingCode(false)
    setCodeError('')
  }, [size, destMode, tracking])
  const saveCode = async () => {
    if (!eventId || !validDest || !codeInput.trim()) return
    setCodeSaving(true)
    setCodeError('')
    try {
      await apiClient.createOrGetShortLink(token!, {
        event: eventId,
        source: SIZE_SOURCE[size],
        destination: debouncedDest,
        code: codeInput.trim(),
      })
      await queryClient.invalidateQueries({ queryKey: ['shortLink'] })
      await queryClient.invalidateQueries({ queryKey: ['shortLinks'] })
      setEditingCode(false)
    } catch (e) {
      const detail = (e as { data?: { detail?: string } })?.data?.detail
      setCodeError(detail || 'Could not update the code.')
    } finally {
      setCodeSaving(false)
    }
  }

  // Inline the logo ONCE as a data URL — the preview iframe is rebuilt on
  // every config change, and a raw URL would re-fetch from the CDN each time
  // (visible pop-in). Data URL also makes the print window network-free.
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null)
  useEffect(() => {
    let active = true
    setLogoDataUrl(null)
    if (!logoUrl) return
    fetch(logoUrl)
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error('logo fetch failed'))))
      .then(
        (blob) =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result as string)
            reader.onerror = reject
            reader.readAsDataURL(blob)
          }),
      )
      .then((dataUrl) => {
        if (active) setLogoDataUrl(dataUrl)
      })
      .catch(() => {
        if (active) setLogoDataUrl(null) // CORS/network — fall back to the raw URL
      })
    return () => {
      active = false
    }
  }, [logoUrl])

  const [showPhoneFrame, setShowPhoneFrame] = useState(true)
  const [showLogo, setShowLogo] = useState(Boolean(logoUrl))
  const [showBadges, setShowBadges] = useState(true)
  const [showUrl, setShowUrl] = useState(true)
  const [qrDataUrl, setQrDataUrl] = useState('')
  const [error, setError] = useState('')

  const qrCache = useRef(new Map<string, string>())
  useEffect(() => {
    if (!qrTarget) {
      setQrDataUrl('')
      return
    }
    // Already generated for this exact target (e.g. flipping back to a size)
    // → reuse instantly, no debounce, no regeneration.
    const cached = qrCache.current.get(qrTarget)
    if (cached) {
      setQrDataUrl(cached)
      return
    }
    const timer = setTimeout(() => {
      QRCode.toDataURL(qrTarget, { width: 768, margin: 0 })
        .then((dataUrl) => {
          qrCache.current.set(qrTarget, dataUrl)
          setQrDataUrl(dataUrl)
        })
        .catch(() => setQrDataUrl(''))
    }, 250)
    return () => clearTimeout(timer)
  }, [qrTarget])

  const config: PosterConfig = {
    size,
    bg,
    headline,
    subline,
    url: qrTarget,
    displayUrl: printedText || destination,
    qrDataUrl,
    eventName,
    logoUrl: logoDataUrl ?? logoUrl,
    showPhoneFrame,
    showLogo,
    showBadges,
    showUrl,
  }

  const previewHtml = qrDataUrl ? buildPosterHtml(config, { preview: true }) : ''

  const dims = POSTER_SIZES[size]
  const wPx = dims.wMm * MM_TO_PX
  const hPx = dims.hMm * MM_TO_PX
  const scale = Math.min(380 / wPx, 1.15)

  const openPrint = () => {
    if (!qrDataUrl) return
    setError('')
    const win = window.open('', '_blank')
    if (!win) {
      setError('Pop-up blocked — allow pop-ups for this site and try again.')
      return
    }
    try {
      openPosterPreview(config, win)
    } catch (e) {
      win.close()
      setError(e instanceof Error ? e.message : 'Could not open the poster preview.')
    }
  }

  const destBtn = (mode: DestMode, label: string, disabled = false) => (
    <button
      key={mode}
      type="button"
      disabled={disabled}
      onClick={() => setDestMode(mode)}
      className={`rounded-md border px-2.5 py-1.5 text-[11px] font-medium transition-colors disabled:opacity-40 ${
        destMode === mode
          ? 'border-blue-600 bg-blue-50 text-blue-700 dark:border-blue-500 dark:bg-blue-950/40 dark:text-blue-300'
          : 'border-neutral-200 text-neutral-600 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800'
      }`}
    >
      {label}
    </button>
  )

  return (
    <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-4 md:p-5">
      <div className="flex flex-col gap-5 lg:flex-row">
        {/* Live preview */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-center rounded-md bg-neutral-100 p-4 dark:bg-neutral-900">
            {previewHtml ? (
              <div style={{ width: wPx * scale, height: hPx * scale, overflow: 'hidden' }}>
                <iframe
                  title="Poster preview"
                  srcDoc={previewHtml}
                  style={{
                    width: wPx,
                    height: hPx,
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                    border: 0,
                    pointerEvents: 'none',
                  }}
                />
              </div>
            ) : (
              <p className="py-16 text-xs text-neutral-500 dark:text-neutral-400">
                {destMode === 'custom'
                  ? 'Enter a destination URL (https://…) to see the preview.'
                  : 'Loading preview…'}
              </p>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="w-full space-y-4 lg:w-72 lg:shrink-0">
          <div>
            <p className="mb-1.5 text-xs font-medium text-neutral-700 dark:text-neutral-200">Size</p>
            <div className="flex flex-wrap gap-1.5">
              {(Object.keys(POSTER_SIZES) as PosterSize[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSize(s)}
                  className={`rounded-md border px-2.5 py-1.5 text-[11px] font-medium transition-colors ${
                    size === s
                      ? 'border-blue-600 bg-blue-50 text-blue-700 dark:border-blue-500 dark:bg-blue-950/40 dark:text-blue-300'
                      : 'border-neutral-200 text-neutral-600 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800'
                  }`}
                >
                  {POSTER_SIZES[s].label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-1.5 text-xs font-medium text-neutral-700 dark:text-neutral-200">Background</p>
            <div className="flex flex-wrap items-center gap-1.5">
              {swatches.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setBg(c)}
                  aria-label={`Background ${c}`}
                  className={`h-7 w-7 rounded-md border ${
                    bg.toLowerCase() === c.toLowerCase()
                      ? 'ring-2 ring-blue-500 ring-offset-1 dark:ring-offset-neutral-900'
                      : 'border-neutral-200 dark:border-neutral-700'
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
              <input
                type="color"
                value={safeHexColor(bg, '#2563eb')}
                onChange={(e) => setBg(e.target.value)}
                aria-label="Custom background color"
                className="h-7 w-7 cursor-pointer rounded-md border border-neutral-200 bg-transparent p-0.5 dark:border-neutral-700"
              />
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-neutral-700 dark:text-neutral-200">
              Headline
              <input value={headline} onChange={(e) => setHeadline(e.target.value)} className={`mt-1.5 ${inputClass}`} maxLength={60} />
            </label>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-neutral-700 dark:text-neutral-200">
              Subline
              <input value={subline} onChange={(e) => setSubline(e.target.value)} className={`mt-1.5 ${inputClass}`} maxLength={80} />
            </label>
          </div>

          {/* Destination + tracking */}
          <div>
            <p className="mb-1.5 text-xs font-medium text-neutral-700 dark:text-neutral-200">Link</p>
            <div className="flex flex-wrap gap-1.5">
              {destBtn('event', 'Event page')}
              {destBtn('group', 'Group page', !groupPageUrl)}
              {destBtn('custom', 'Custom')}
            </div>
            {destMode === 'custom' && (
              <input
                value={customDest}
                onChange={(e) => setCustomDest(e.target.value)}
                className={`mt-1.5 ${inputClass}`}
                placeholder="https://…"
              />
            )}
            <div className="mt-1.5 rounded-md border border-neutral-200 px-2.5 py-2 text-[10px] text-neutral-500 dark:border-neutral-800 dark:text-neutral-400">
              {!tracking ? (
                <span>Tracking off — the QR points directly at the destination.</span>
              ) : link.data ? (
                <div className="flex items-center gap-1.5">
                  <span className="min-w-0 flex-1 truncate font-mono text-[10px] text-neutral-600 dark:text-neutral-300" title={link.data.short_url}>
                    {link.data.short_url.replace(/^https?:\/\//i, '')}
                  </span>
                  <span className="shrink-0 tabular-nums">{link.data.hits.toLocaleString()} scan{link.data.hits === 1 ? '' : 's'}</span>
                  <button
                    type="button"
                    aria-label="Edit short-link code"
                    onClick={() => {
                      setCodeInput(link.data!.code)
                      setCodeError('')
                      setEditingCode(true)
                    }}
                    className="shrink-0 rounded p-0.5 text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                </div>
              ) : link.isError ? (
                <span className="text-amber-600 dark:text-amber-400">
                  Tracking error — the QR points directly at the destination for now. Retry or check your connection.
                </span>
              ) : link.data === null ? (
                <span className="text-amber-600 dark:text-amber-400">
                  Tracking isn&rsquo;t available on this server yet — the QR points directly at the destination.
                </span>
              ) : (
                <span>{validDest ? 'Tracked QR — setting up…' : 'Enter a valid https:// destination.'}</span>
              )}
              {tracking && editingCode && (
                <div className="mt-1.5">
                  <div className="flex items-center gap-1">
                    <span className="shrink-0 font-mono">/r/</span>
                    <input
                      value={codeInput}
                      onChange={(e) => setCodeInput(e.target.value)}
                      className={inputClass}
                      placeholder="vip-entry"
                    />
                    <button
                      type="button"
                      onClick={saveCode}
                      disabled={codeSaving || !codeInput.trim()}
                      className="shrink-0 rounded-md bg-blue-600 px-2 py-1 text-[10px] font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      {codeSaving ? '…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingCode(false)}
                      className="shrink-0 rounded-md border border-neutral-200 px-2 py-1 text-[10px] text-neutral-600 dark:border-neutral-700 dark:text-neutral-300"
                    >
                      Cancel
                    </button>
                  </div>
                  {codeError && <p className="mt-1 text-[10px] text-red-600 dark:text-red-400">{codeError}</p>}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-neutral-700 dark:text-neutral-200">
              Printed link text
              <input
                value={printedText}
                onChange={(e) => {
                  setPrintedText(e.target.value)
                  setTextDirty(true)
                }}
                className={`mt-1.5 ${inputClass}`}
                placeholder="pay.eventpay.com/event/…"
                disabled={!showUrl}
              />
            </label>
            <p className="mt-1 text-[10px] text-neutral-500 dark:text-neutral-400">
              What guests read (and can type). The QR still tracks scans.
            </p>
          </div>

          <div className="space-y-2 rounded-md border border-neutral-200 p-3 dark:border-neutral-800">
            <DesignerToggle label="Track scans" checked={tracking} onChange={setTracking} />
            <DesignerToggle label="Phone frame" checked={showPhoneFrame} onChange={setShowPhoneFrame} disabled={size !== 'a4'} />
            <DesignerToggle label="Event logo" checked={showLogo} onChange={setShowLogo} disabled={!logoUrl} />
            <DesignerToggle label="App store badges" checked={showBadges} onChange={setShowBadges} disabled={size === 'business-card'} />
            <DesignerToggle label="Show link text" checked={showUrl} onChange={setShowUrl} />
          </div>

          <button
            type="button"
            onClick={openPrint}
            disabled={!qrDataUrl}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <Printer className="h-3.5 w-3.5" /> Print / Save as PDF
          </button>
          {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
        </div>
      </div>
    </div>
  )
}

// --- Best-practices content (from src/lib/toolkit/resources.ts) ------------

const AUDIENCES: { key: ResourceAudience; label: string; icon: React.ReactNode }[] = [
  { key: 'attendees', label: 'For Attendees', icon: <Users className="h-4 w-4" /> },
  { key: 'vendors', label: 'For Vendors', icon: <Store className="h-4 w-4" /> },
  { key: 'team', label: 'For Your Team', icon: <Users2 className="h-4 w-4" /> },
]

/** Render markdown-lite: consecutive "- " lines become a list, other
 *  non-empty lines become paragraphs. Keeps launch dependency-free. */
function BodyBlocks({ body }: { body: string }) {
  const nodes: React.ReactNode[] = []
  let list: string[] = []
  const flush = () => {
    if (list.length) {
      nodes.push(
        <ul key={`ul-${nodes.length}`} className="list-disc space-y-1 pl-4">
          {list.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>,
      )
      list = []
    }
  }
  body.split('\n').forEach((raw) => {
    const line = raw.trim()
    if (line.startsWith('- ')) {
      list.push(line.slice(2))
    } else if (line === '') {
      flush()
    } else {
      flush()
      nodes.push(<p key={`p-${nodes.length}`}>{line}</p>)
    }
  })
  flush()
  return <div className="space-y-2 text-xs leading-relaxed text-neutral-600 dark:text-neutral-300">{nodes}</div>
}

function BestPractices({ eventName, tokenName }: { eventName: string; tokenName: string }) {
  const [openKey, setOpenKey] = useState<string | null>(null)

  const merge = (s: string) =>
    s.replaceAll('{event_name}', eventName).replaceAll('{token_name}', tokenName)

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      {AUDIENCES.map((a) => (
        <div
          key={a.key}
          className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-4"
        >
          <div className="flex items-center gap-2 text-neutral-900 dark:text-neutral-100">
            <span className="text-blue-600 dark:text-blue-400">{a.icon}</span>
            <h3 className="text-sm font-semibold">{a.label}</h3>
          </div>
          <ul className="mt-3 space-y-1">
            {TOOLKIT_RESOURCES.filter((r) => r.audience === a.key)
              .sort((x, y) => x.order - y.order)
              .map((r) => {
                const key = `${a.key}:${r.title}`
                const open = openKey === key
                return (
                  <li key={key} className="-mx-2">
                    <button
                      type="button"
                      onClick={() => setOpenKey(open ? null : key)}
                      aria-expanded={open}
                      className="w-full rounded-md px-2 py-2 text-left transition-colors hover:bg-neutral-50 dark:hover:bg-neutral-900"
                    >
                      <span className="flex items-start justify-between gap-2">
                        <span>
                          <span className="block text-sm font-medium text-neutral-800 dark:text-neutral-100">
                            {r.title}
                          </span>
                          <span className="block text-xs text-neutral-500 dark:text-neutral-400">
                            {r.summary}
                          </span>
                        </span>
                        <ChevronDown
                          className={`mt-1 h-3.5 w-3.5 shrink-0 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`}
                        />
                      </span>
                    </button>
                    {open && (
                      <div className="px-2 pb-2 pt-1">
                        <BodyBlocks body={merge(r.body)} />
                      </div>
                    )}
                  </li>
                )
              })}
          </ul>
        </div>
      ))}
    </div>
  )
}

// --- Page ------------------------------------------------------------------

function ToolkitPage() {
  const { eventId } = Route.useParams()
  const canView = usePermission('can_view_toolkit')
  const { data: event } = useEvent(Number(eventId))

  const eventBase = event ? slugify(event.name || `event-${eventId}`) : `event-${eventId}`
  // Built client-side from data the API already returns — no backend changes
  // needed: event id (always present) + org slug (fetched by org id).
  const orgId =
    typeof event?.organization === 'number' ? event.organization : event?.organization?.id
  const { data: org } = useOrganizationDetail(orgId)
  const eventShareUrl = event ? `${PUBLIC_WEB_BASE}/event/${event.id}` : null
  const groupShareUrl = org?.slug ? `${PUBLIC_WEB_BASE}/groups/${org.slug}` : null

  // Tracked short links per surface (create-or-get; null → backend without
  // the endpoint yet, everything falls back to the direct URLs).
  const eventIdNum = event?.id
  const shareEventLink = useShortLink(eventIdNum, 'share-event', eventShareUrl)
  const shareGroupLink = useShortLink(eventIdNum, 'share-group', groupShareUrl)
  // Per-surface scan totals for the Posters section header.
  const { data: allLinks, isError: linksError } = useShortLinksList(eventIdNum)

  if (!canView) return <AccessDenied />

  return (
    <div className="mx-auto w-full max-w-[1300px] px-4 md:px-8">
      <div className="py-6">
        <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">Toolkit</h1>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          Everything to promote {event?.name || 'your event'} and help guests, vendors, and your team.
        </p>
      </div>

      {/* Share & QR */}
      <section className="mb-8">
        <h2 className="mb-3 text-lg font-medium text-neutral-900 dark:text-neutral-100">Share & QR</h2>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <ShareCard
            label="Event link"
            description="Send attendees straight to this event."
            url={shareEventLink.data?.short_url ?? eventShareUrl}
            filenameBase={`${eventBase}-event`}
            hits={shareEventLink.data?.hits}
          />
          <ShareCard
            label="Group link"
            description="Your organization page — all your events in one place."
            url={shareGroupLink.data?.short_url ?? groupShareUrl}
            filenameBase={`${eventBase}-group`}
            hits={shareGroupLink.data?.hits}
          />
        </div>
      </section>

      {/* Poster designer */}
      <section className="mb-8">
        <h2 className="mb-1 text-lg font-medium text-neutral-900 dark:text-neutral-100">Posters & print</h2>
        <p className="mb-1 text-sm text-neutral-500 dark:text-neutral-400">
          Design a &ldquo;scan to pay&rdquo; poster, postcard, or booth card — print-ready PDF at exact size.
        </p>
        {(() => {
          if (linksError) {
            return (
              <p className="mb-3 text-xs text-amber-600 dark:text-amber-400">
                Scan totals couldn&rsquo;t load right now.
              </p>
            )
          }
          const bySource = Object.fromEntries((allLinks ?? []).map((l) => [l.source, l.hits]))
          const rows = [
            ['poster', 'Poster'],
            ['postcard', 'Postcard'],
            ['card', 'Booth card'],
          ].filter(([key]) => bySource[key] !== undefined)
          if (rows.length === 0) return <div className="mb-3" />
          return (
            <p className="mb-3 text-xs tabular-nums text-neutral-500 dark:text-neutral-400">
              Scans —{' '}
              {rows.map(([key, label], i) => (
                <span key={key}>
                  {i > 0 && ' · '}
                  {label} <span className="font-medium text-neutral-700 dark:text-neutral-200">{bySource[key].toLocaleString()}</span>
                </span>
              ))}
            </p>
          )
        })()}
        <PosterDesigner
          eventId={eventIdNum}
          eventName={event?.name || 'Your event'}
          eventPageUrl={eventShareUrl}
          groupPageUrl={groupShareUrl}
          logoUrl={event?.logo}
          accentColor={event?.accent_color}
        />
      </section>

      {/* Best Practices */}
      <section className="mb-10">
        <h2 className="mb-3 text-lg font-medium text-neutral-900 dark:text-neutral-100">Best Practices</h2>
        <BestPractices
          eventName={event?.name || 'your event'}
          tokenName={event?.token_name || 'tokens'}
        />
      </section>
    </div>
  )
}
